import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import map from 'lodash/map';
import classnames from 'classnames';
import StructureContext from '@Components/Results/structure/StructureContext';
import StructurePreview from '@Components/Results/structure/StructurePreview';
import { getSynthesisMenuOptionsForRouteType } from '@Pages/RetroSynthesis/StructureOption/utils';
import { PATH_PLANNER_S } from '@Pages/RetroSynthesis/constants';
import * as FormatUtils from '@Services/FormatUtils';
import StructureModel from '@Components/Results/structure/structure-model.service';
import htmlStripsService from '@Services/HtmlStripsService';
import RsStructureToolbar from './RsStructureToolbar';

import './RsStructure.scss';

/**
 * Format availability links and images urls and add access token for BE
 * @param availability
 */

export function formatStructureAvailabilityUrls(availability) {
  if (!availability) return availability;
  return {
    ...availability,
    ...(availability.url && { url: FormatUtils.formatPath(availability.url) }),
    ...(availability.providers && {
      providers: map(availability.providers, (provider) => {
        if (provider.custom) return provider;
        return {
          ...provider,
          imageUrl: FormatUtils.formatPath(provider.imageUrl),
          linkUrl: FormatUtils.formatPath(provider.linkUrl),
        };
      }),
    }),
  };
}

function getContextValue(props) {
  if (!props.structure) {
    Logger.warn('RsStructure - structure is missing');
  }

  const { databaseId, supplierNumber, rxn, path, structure, subDetails, subLinks, availability, customerInfo } = props.structure || {};

  const structureForModel = {
    databaseId,
    supplierNumber,
    rxn,
    molImage: {
      path,
      structure,
    },
    details: subDetails || {},
    subLinks,
    availability: formatStructureAvailabilityUrls(availability),
    customerInfo,
  };
  return {
    metadata: {
      fromSynthesisPlanner: true,
    },
    structure: new StructureModel(structureForModel),
  };
}

export default class RsStructure extends React.PureComponent {
  constructor(props) {
    super(props);
    this.previewProps = this.getPreviewProps();
    this.structureContextValue = getContextValue(props);
  }

  // useMemo when migrating to functional component
  componentDidUpdate(prevProps) {
    // eslint-disable-next-line react/destructuring-assignment
    if (this.props.structure !== prevProps.structure) {
      this.structureContextValue = getContextValue(this.props);
    }
  }

  getPreviewProps() {
    let previewProps = {};
    const details = get(this.props, 'structure.subDetails');

    if (details) {
      previewProps = {
        mixtureName: details.mixtureItems ? details.type : null,
        structureId: details.id,
        title: htmlStripsService.getTitleWithoutHtml(details.linearFormula, 'full'),
      };
    }
    return previewProps;
  }

  render() {
    const { props } = this;
    const imagePath = get(props, 'structure.path');
    const { showToolbar } = props;
    const className = classnames([props.className, 'substance-image-container rs-substance-image-container']);
    const chemicalNames = get(props, 'structure.subDetails.chemicalNames');
    const synthesisMenuOptions = getSynthesisMenuOptionsForRouteType(props.synthesisMenuOptions, props.routeType);
    return (
      <StructureContext.Provider value={this.structureContextValue}>
        <div className={className}>
          <StructurePreview
            previewPathKey={props.imagePathKey}
            previewPath={imagePath}
            {...this.previewProps}
            chemicalNames={chemicalNames}
          />
          {showToolbar ? (
            <RsStructureToolbar
              synthesisMenuOptions={synthesisMenuOptions}
              onOptionOpen={props.onToolbarOptionOpen}
              onMenuItemClick={props.onMenuItemClick}
              onOpenInDatabaseClick={props.onOpenInDatabaseClick}
              onAutoSynthesizeClick={props.onAutoSynthesizeClick}
              onShoppingCartOptionClick={props.onShoppingCartOptionClick}
              showOptions={props.showOptions}
              showZoom={props.showZoom}
              showDetails={props.showDetails}
              trackingContent={props.trackingContent}
            />
          ) : null}
        </div>
      </StructureContext.Provider>
    );
  }
}

RsStructure.defaultProps = {
  showToolbar: true,
  showOptions: true,
  showZoom: true,
  showDetails: true,
  imagePathKey: PATH_PLANNER_S,
  onToolbarOptionOpen: () => null,
  onMenuItemClick: () => null,
  onOpenInDatabaseClick: () => null,
  onAutoSynthesizeClick: () => null,
  onShoppingCartOptionClick: () => null,
};

RsStructure.propTypes = {
  routeType: PropTypes.string,
  structure: PropTypes.object,
  trackingContent: PropTypes.array,
  className: PropTypes.string,
  showToolbar: PropTypes.bool,
  showZoom: PropTypes.bool,
  showOptions: PropTypes.bool,
  showDetails: PropTypes.bool,
  synthesisMenuOptions: PropTypes.array,
  imagePathKey: PropTypes.string,
  onToolbarOptionOpen: PropTypes.func,
  onMenuItemClick: PropTypes.func,
  onOpenInDatabaseClick: PropTypes.func,
  onAutoSynthesizeClick: PropTypes.func,
  onShoppingCartOptionClick: PropTypes.func,
};
